import React, { useEffect, useRef } from "react";
import makeStyles from "@mui/styles/makeStyles";

const useStyles = makeStyles((theme) => ({
  canvas: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    zIndex: 0,
    pointerEvents: "none",
  },
  desktopOnly: {
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },
}));

/**
 * Per-section floating 3D props (variant picks the prop set). three.js is
 * imported dynamically after mount and rendering pauses while the section is
 * off screen. Silently absent when WebGL is unavailable.
 */
function SectionBackground3D({ variant }) {
  const classes = useStyles();
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    let api = null;
    let stopped = false;
    let observer = null;

    const onResize = () => {
      if (!api || !canvas.parentElement) return;
      const { clientWidth, clientHeight } = canvas.parentElement;
      api.resize(clientWidth, clientHeight);
    };

    const mobile =
      window.matchMedia && window.matchMedia("(max-width: 899.95px)").matches;
    // On phones only the dark-band route runs; the other sections stay 2D
    // (their canvases are display:none, so skip creating a scene at all).
    if (mobile && variant !== "about") return undefined;

    import("../Home3D/sectionScene")
      .then((mod) => {
        if (stopped || !canvasRef.current) return;
        try {
          api = mod.createSectionScene(canvas, variant, { mobile });
        } catch (e) {
          canvas.style.display = "none";
          return;
        }
        onResize();
        window.addEventListener("resize", onResize);
        if (typeof IntersectionObserver !== "undefined") {
          observer = new IntersectionObserver(
            ([entry]) => api && api.setVisible(entry.isIntersecting),
            { threshold: 0 }
          );
          observer.observe(canvas);
        }
      })
      .catch(() => {
        canvas.style.display = "none";
      });

    return () => {
      stopped = true;
      window.removeEventListener("resize", onResize);
      if (observer) observer.disconnect();
      if (api) api.dispose();
    };
  }, [variant]);

  return (
    <canvas
      ref={canvasRef}
      className={`${classes.canvas} ${
        variant === "about" ? "" : classes.desktopOnly
      }`}
      aria-hidden="true"
    />
  );
}

export default SectionBackground3D;